import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Paper, Typography, TextField, Button, Grid,
    Autocomplete, CircularProgress, Alert, Snackbar,
    Card, CardContent, Divider, IconButton, Chip, Dialog,
    DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SearchIcon from '@mui/icons-material/Search';
import { useEvent } from '../../contexts/event.context';
import { maskCPF2, maskCNPJ } from '../../utils/mask';
import { validateBr } from 'js-brasil';
import api from '../../services/api';
import * as qs from 'qs';

export default function InscricaoRapida() {
    const { events, getEvents } = useEvent();
    const [eventSelected, setEventSelected] = useState(null);
    const [eventOptions, setEventOptions] = useState([]);
    const [loadingEvents, setLoadingEvents] = useState(false);
    const [plans, setPlans] = useState([]);
    const [selectedPlan, setSelectedPlan] = useState(null);
    
    // Dados da empresa
    const [cnpj, setCnpj] = useState('');
    const [razaoSocial, setRazaoSocial] = useState('');
    const [nomeFantasia, setNomeFantasia] = useState('');
    const [cep, setCep] = useState('');
    const [endereco, setEndereco] = useState('');
    const [numero, setNumero] = useState('');
    const [bairro, setBairro] = useState('');
    const [cidade, setCidade] = useState('');
    const [estado, setEstado] = useState('');
    const [emailEmpresa, setEmailEmpresa] = useState('');
    const [loadingCompany, setLoadingCompany] = useState(false);
    const [loadingCep, setLoadingCep] = useState(false);
    const [openCompanySearchModal, setOpenCompanySearchModal] = useState(false);
    const [companySearchName, setCompanySearchName] = useState('');
    const [companySearchResults, setCompanySearchResults] = useState([]);
    const [companySearchLoading, setCompanySearchLoading] = useState(false);
    const [companySearchAttempted, setCompanySearchAttempted] = useState(false);
    
    // Inscritos
    const [subscribers, setSubscribers] = useState([{
        id: Date.now(),
        document: '',
        name: '',
        whatsapp: '',
        email: '',
        badge: '',
        birth: '',
        gender: '',
        office: '',
        tshirtSize: ''
    }]);
    const [loadingSubscriber, setLoadingSubscriber] = useState({});
    
    const [loading, setLoading] = useState(false);
    const [stateAlert, setStateAlert] = useState({ status: false, type: 'success', msg: '' });
    const [openPrintModal, setOpenPrintModal] = useState(false);
    const [pdfData, setPdfData] = useState(null);
    const printIframeRef = useRef(null);

    useEffect(() => {
        async function loadInitialEvents() {
            setLoadingEvents(true);
            try {
                await getEvents(1, 50);
                setEventOptions(events);
            } catch (error) {
                console.error('Erro ao carregar eventos:', error);
            } finally {
                setLoadingEvents(false);
            }
        }
        loadInitialEvents();
    }, []);

    useEffect(() => {
        if (events && events.length > 0) {
            setEventOptions(events);
        }
    }, [events]);

    useEffect(() => {
        if (eventSelected) {
            loadPlans();
        }
    }, [eventSelected]);

    async function loadPlans() {
        try {
            const params = {
                filters: {
                    name: { $eq: eventSelected }
                },
                populate: {
                    plans: {
                        fields: ['type', 'price', 'status']
                    }
                }
            };

            const query = qs.stringify(params);
            const response = await api.get(`/events?${query}`);

            if (response.data.data.length > 0) {
                const eventPlans = response.data.data[0].attributes?.plans?.data || [];
                setPlans(eventPlans);
            }
        } catch (error) {
            console.error('Erro ao carregar planos:', error);
            setStateAlert({ status: true, type: 'error', msg: 'Erro ao carregar planos do evento' });
        }
    }

    async function handleCepChange(value) {
        const cepNumbers = value.replace(/\D/g, '');
        const maskedCep = cepNumbers.length > 5 
            ? `${cepNumbers.slice(0, 5)}-${cepNumbers.slice(5, 8)}` 
            : cepNumbers;
        setCep(maskedCep);

        if (cepNumbers.length === 8) {
            setLoadingCep(true);
            try {
                const response = await fetch(`https://viacep.com.br/ws/${cepNumbers}/json/`);
                const data = await response.json();

                if (!data.erro) {
                    setEndereco(data.logradouro || '');
                    setBairro(data.bairro || '');
                    setCidade(data.localidade || '');
                    setEstado(data.uf || '');
                    setStateAlert({ status: true, type: 'success', msg: 'CEP encontrado!' });
                } else {
                    setStateAlert({ status: true, type: 'warning', msg: 'CEP não encontrado' });
                }
            } catch (error) {
                console.error('Erro ao buscar CEP:', error);
                setStateAlert({ status: true, type: 'error', msg: 'Erro ao buscar CEP' });
            } finally {
                setLoadingCep(false);
            }
        }
    }

    async function handleCNPJChange(value) {
        const masked = maskCNPJ(value);
        setCnpj(masked);

        if (masked.length === 18 && validateBr.cnpj(masked)) {
            setLoadingCompany(true);
            try {
                const response = await api.get(`/companies/${masked.replace('/', '%2F')}`);
                if (response.data) {
                    const company = response.data;
                    setRazaoSocial(company.social_reason || '');
                    setNomeFantasia(company.trade || '');
                    setCep(company.cep || '');
                    setEndereco(company.address || '');
                    setNumero(company.number || '');
                    setBairro(company.district || '');
                    setCidade(company.city || '');
                    setEstado(company.state || '');
                    setEmailEmpresa(company.email || '');
                    setStateAlert({ status: true, type: 'success', msg: 'Empresa encontrada!' });
                } else {
                    setRazaoSocial('');
                    setNomeFantasia('');
                    setCep('');
                    setEndereco('');
                    setNumero('');
                    setBairro('');
                    setCidade('');
                    setEstado('');
                    setEmailEmpresa('');
                    setStateAlert({ status: true, type: 'info', msg: 'Empresa não encontrada. Preencha os dados manualmente.' });
                }
            } catch (error) {
                console.error('Erro ao buscar empresa:', error);
                setStateAlert({ status: true, type: 'error', msg: 'Erro ao buscar empresa' });
            } finally {
                setLoadingCompany(false);
            }
        }
    }

    function fillCompanyData(companyAttributes = {}) {
        const doc = companyAttributes.document || '';
        setCnpj(maskCNPJ(doc));
        setRazaoSocial(companyAttributes.social_reason || '');
        setNomeFantasia(companyAttributes.trade || '');
        setCep(companyAttributes.cep || '');
        setEndereco(companyAttributes.address || '');
        setNumero(companyAttributes.number || '');
        setBairro(companyAttributes.district || '');
        setCidade(companyAttributes.city || '');
        setEstado(companyAttributes.state || '');
        setEmailEmpresa(companyAttributes.email || '');
    }

    async function handleSearchCompanyByName() {
        const searchTerm = companySearchName.trim();
        if (searchTerm.length < 2) {
            setStateAlert({ status: true, type: 'warning', msg: 'Digite ao menos 2 caracteres para pesquisar empresa.' });
            return;
        }

        setCompanySearchLoading(true);
        setCompanySearchAttempted(true);
        try {
            const query = qs.stringify({
                filters: {
                    $or: [
                        { social_reason: { $containsi: searchTerm } },
                        { trade: { $containsi: searchTerm } },
                    ],
                },
                sort: ['social_reason:asc'],
                pagination: { page: 1, pageSize: 20 },
                encodeValuesOnly: true,
            });

            const response = await api.get('/companies?' + query);
            setCompanySearchResults(response.data.data || []);
        } catch (error) {
            console.error('Erro ao pesquisar empresa:', error);
            setCompanySearchResults([]);
            setStateAlert({ status: true, type: 'error', msg: 'Erro ao pesquisar empresa por nome.' });
        } finally {
            setCompanySearchLoading(false);
        }
    }

    function handleSelectCompanyFromSearch(company) {
        const attrs = company?.attributes || {};
        fillCompanyData(attrs);
        setOpenCompanySearchModal(false);
        setStateAlert({ status: true, type: 'success', msg: 'Empresa selecionada com sucesso!' });
    }

    function handleAddSubscriber() {
        setSubscribers([...subscribers, {
            id: Date.now(),
            document: '',
            name: '',
            whatsapp: '',
            email: '',
            badge: '',
            birth: '',
            gender: '',
            office: '',
            tshirtSize: ''
        }]);
    }

    function handleRemoveSubscriber(id) {
        if (subscribers.length > 1) {
            setSubscribers(subscribers.filter(sub => sub.id !== id));
        }
    }

    async function handleSubscriberChange(id, field, value) {
        setSubscribers(subscribers.map(sub => {
            if (sub.id === id) {
                if (field === 'document') {
                    return { ...sub, [field]: maskCPF2(value) };
                }
                return { ...sub, [field]: value };
            }
            return sub;
        }));

        // Se for alteração de CPF, buscar dados do subscriber
        if (field === 'document') {
            const maskedCpf = maskCPF2(value);
            if (maskedCpf.length === 14 && validateBr.cpf(maskedCpf)) {
                setLoadingSubscriber(prev => ({ ...prev, [id]: true }));
                try {
                    const { data } = await api.post(`/subscriber/form`, { 
                        data: { cpf: maskedCpf } 
                    });
                    
                    if (data && data?.name?.length > 0) {
                        setSubscribers(prevSubs => prevSubs.map(sub => {
                            if (sub.id === id) {
                                return {
                                    ...sub,
                                    name: data.name || '',
                                    badge: data.badge || '',
                                    email: data.email || '',
                                    birth: data.birth || '',
                                    gender: data.gender || '',
                                    whatsapp: data.whatsapp || '',
                                    office: data.office || '',
                                    tshirtSize: data.tshirtSize || ''
                                };
                            }
                            return sub;
                        }));
                        setStateAlert({ status: true, type: 'success', msg: 'Dados do inscrito encontrados!' });
                    } else {
                        setStateAlert({ status: true, type: 'info', msg: 'CPF não encontrado. Preencha os dados manualmente.' });
                    }
                } catch (error) {
                    console.error('Erro ao buscar inscrito:', error);
                    setStateAlert({ status: true, type: 'info', msg: 'Inscrito não encontrado. Preencha os dados manualmente.' });
                } finally {
                    setLoadingSubscriber(prev => ({ ...prev, [id]: false }));
                }
            }
        }
    }

    async function handleSubmit() {
        // Validações
        if (!eventSelected) {
            setStateAlert({ status: true, type: 'warning', msg: 'Selecione um evento' });
            return;
        }

        if (!selectedPlan) {
            setStateAlert({ status: true, type: 'warning', msg: 'Selecione um plano/categoria' });
            return;
        }

        if (!cnpj || cnpj.length !== 18) {
            setStateAlert({ status: true, type: 'warning', msg: 'CNPJ inválido' });
            return;
        }

        if (!razaoSocial || !cep || !endereco || !numero || !bairro || !cidade || !estado || !emailEmpresa) {
            setStateAlert({ status: true, type: 'warning', msg: 'Preencha todos os dados da empresa (CEP, endereço, número, bairro, cidade, estado e email são obrigatórios)' });
            return;
        }

        const invalidSubscriber = subscribers.find(sub => 
            !sub.document || 
            sub.document.length !== 14 || 
            !validateBr.cpf(sub.document) ||
            !sub.name ||
            !sub.whatsapp
        );

        if (invalidSubscriber) {
            setStateAlert({ status: true, type: 'warning', msg: 'Preencha corretamente os dados de todos os inscritos (CPF válido, nome e WhatsApp são obrigatórios)' });
            return;
        }

        setLoading(true);

        try {
            // 1. Buscar ou criar empresa
            let companyData;
            try {
                const companyResponse = await api.get(`/companies/${cnpj.replace('/', '%2F')}`);
                companyData = companyResponse?.data;
            } catch {
                // Se nao encontrar no endpoint customizado, cria empresa no Strapi.
                const newCompany = await api.post('/companies', {
                    data: {
                        document: cnpj,
                        social_reason: razaoSocial,
                        trade: nomeFantasia || razaoSocial,
                        cep: cep,
                        address: endereco,
                        number: numero,
                        district: bairro,
                        city: cidade,
                        state: estado,
                        email: emailEmpresa
                    }
                });
                companyData = newCompany?.data?.data;
            }

            const companyAttributes = companyData?.attributes || companyData || {};

            // 2. Buscar evento
            const eventParams = {
                filters: {
                    name: { $eq: eventSelected }
                }
            };

            const eventQuery = qs.stringify(eventParams);
            const eventResponse = await api.get(`/events?${eventQuery}`);
            const eventId = eventResponse.data.data[0].id;

            // 3. Criar pedido
            const planPrice = plans.find(p => p.id === selectedPlan)?.attributes?.price || 0;
            const paymentForm = planPrice === 0 ? 'GRATUITO' : 'BOLETO';

            const orderData = {
                data: {
                    event: eventId,
                    company: {
                        document: companyAttributes.document || cnpj,
                        social_reason: companyAttributes.social_reason || razaoSocial,
                        trade: companyAttributes.trade || nomeFantasia || razaoSocial,
                        cep: companyAttributes.cep || cep,
                        address: companyAttributes.address || endereco,
                        number: companyAttributes.number || numero,
                        district: companyAttributes.district || bairro,
                        city: companyAttributes.city || cidade,
                        state: companyAttributes.state || estado,
                        email: companyAttributes.email || emailEmpresa
                    },
                    plan: selectedPlan,
                    plan_price: planPrice,
                    subscribers: subscribers.map(sub => ({
                        document: sub.document,
                        name: sub.name,
                        whatsapp: sub.whatsapp,
                        email: sub.email || null,
                        badge: sub.badge || null,
                        birth: sub.birth || null,
                        gender: sub.gender || null,
                        office: sub.office || null,
                        tshirtSize: sub.tshirtSize || null
                    })),
                    quantity: subscribers.length,
                    payment_form: paymentForm
                }
            };

            const orderResponse = await api.post('/orders', orderData);

            setStateAlert({ status: true, type: 'success', msg: 'Inscrição realizada com sucesso!' });

            // Imprimir crachás automaticamente
            await handlePrintBadges(orderResponse.data);
            
            // Limpar formulário
            setCnpj('');
            setRazaoSocial('');
            setNomeFantasia('');
            setCep('');
            setEndereco('');
            setNumero('');
            setBairro('');
            setCidade('');
            setEstado('');
            setEmailEmpresa('');
            setSubscribers([{
                id: Date.now(),
                document: '',
                name: '',
                whatsapp: '',
                email: '',
                badge: '',
                birth: '',
                gender: '',
                office: '',
                tshirtSize: ''
            }]);
            setSelectedPlan(null);

        } catch (error) {
            console.error('Erro ao realizar inscrição:', error);
            setStateAlert({ status: true, type: 'error', msg: `Erro ao realizar inscrição: ${error.response?.data?.error?.message || error.message}` });
        } finally {
            setLoading(false);
        }
    }

    function extractTransaction(orderData) {
        if (!orderData) return '';
        if (orderData['0']?.transaction) return orderData['0'].transaction;
        if (orderData.data?.transaction) return orderData.data.transaction;
        if (orderData.transaction) return orderData.transaction;
        if (Array.isArray(orderData) && orderData[0]?.transaction) return orderData[0].transaction;
        return '';
    }

    function extractSubscriberIds(orderData) {
        if (!orderData) return [];

        const fromNode = (node) => {
            if (!node) return [];
            if (Array.isArray(node.subscribers)) {
                return node.subscribers.map((sub) => sub?.id || sub).filter(Boolean).map(Number);
            }
            if (Array.isArray(node?.attributes?.subscribers?.data)) {
                return node.attributes.subscribers.data.map((sub) => sub?.id).filter(Boolean).map(Number);
            }
            return [];
        };

        return [
            ...fromNode(orderData),
            ...fromNode(orderData.data),
            ...fromNode(orderData['0']),
        ].filter(Boolean);
    }

    async function fetchSubscriberIdsByTransaction(transaction) {
        const params = {
            filters: { transaction: { $eq: transaction } },
            populate: { subscribers: { fields: ['id'] } },
            pagination: { page: 1, pageSize: 1 },
            encodeValuesOnly: true,
        };
        const query = qs.stringify(params);
        const response = await api.get(`/orders?${query}`);
        const order = response?.data?.data?.[0];
        return order?.attributes?.subscribers?.data?.map((sub) => sub.id) || [];
    }

    async function resolveSubscriberIds(orderData) {
        let ids = extractSubscriberIds(orderData);
        if (ids.length > 0) {
            return Array.from(new Set(ids));
        }

        const transaction = extractTransaction(orderData);
        if (!transaction) return [];

        // Backend pode demorar alguns ms para relacionar todos os subscribers.
        for (let attempt = 0; attempt < 3; attempt += 1) {
            const fetchedIds = await fetchSubscriberIdsByTransaction(transaction);
            if (fetchedIds.length > 0) {
                return Array.from(new Set(fetchedIds));
            }
            await new Promise((resolve) => setTimeout(resolve, 400));
        }

        return [];
    }

    async function handlePrintBadges(orderData) {
        try {
            const subscriberIds = await resolveSubscriberIds(orderData);

            if (subscriberIds.length === 0) {
                throw new Error('Nao foi possivel identificar os inscritos para impressao.');
            }

            if (subscriberIds.length === 1) {
                await printSingleBadge(subscriberIds[0]);
            } else {
                await printMultipleBadges(subscriberIds);
            }
        } catch (error) {
            console.error('Erro ao imprimir crachás:', error);
            setStateAlert({ status: true, type: 'warning', msg: 'Inscrição realizada, mas houve erro ao gerar os crachás.' });
        }
    }

    async function printSingleBadge(subscriberId) {
        try {
            const response = await api.post(`order/label`, { subscriber: subscriberId, duplicate: true });

            if (response.status === 200) {
                const base64Pdf = response.data.pdf;
                
                const byteCharacters = atob(base64Pdf);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                
                setPdfData(url);
                setOpenPrintModal(true);
            }
        } catch (error) {
            console.error('Erro ao gerar crachá:', error);
            throw error;
        }
    }

    async function printMultipleBadges(subscriberIds) {
        try {
            const response = await api.post(`order/selected-labels`, { subscribers: subscriberIds, duplicate: true });

            if (response.status === 200) {
                const base64Pdf = response.data.pdf;
                
                const byteCharacters = atob(base64Pdf);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                
                setPdfData(url);
                setOpenPrintModal(true);
            }
        } catch (error) {
            console.error('Erro ao gerar crachás:', error);
            throw error;
        }
    }

    function handlePrint() {
        if (printIframeRef.current) {
            try {
                printIframeRef.current.contentWindow.print();
            } catch (error) {
                console.error('Erro ao imprimir:', error);
                setStateAlert({ status: true, type: 'error', msg: 'Erro ao enviar para impressão' });
            }
        }
    }

    function handleCloseModal() {
        setOpenPrintModal(false);
        if (pdfData) {
            URL.revokeObjectURL(pdfData);
        }
        setPdfData(null);
    }

    // Mantem comportamento normal da tela: abrir PDF e disparar impressao automaticamente.
    useEffect(() => {
        if (openPrintModal && pdfData && printIframeRef.current) {
            const iframe = printIframeRef.current;
            const handleLoad = () => {
                setTimeout(() => {
                    try {
                        iframe.contentWindow.print();
                    } catch (error) {
                        console.error('Erro ao imprimir automaticamente:', error);
                    }
                }, 500);
            };

            iframe.addEventListener('load', handleLoad);
            return () => iframe.removeEventListener('load', handleLoad);
        }

        return undefined;
    }, [openPrintModal, pdfData]);

    return (
        <Box sx={{ width: '100%', minHeight: '100vh', backgroundColor: '#f5f5f5', p: 3 }}>
            <Snackbar
                open={stateAlert.status}
                autoHideDuration={4000}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                onClose={() => setStateAlert({ status: false, type: 'success', msg: '' })}
            >
                <Alert sx={{ width: '100%' }} severity={stateAlert.type}>
                    {stateAlert.msg}
                </Alert>
            </Snackbar>

            <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                    <PersonAddIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Inscrição Rápida
                </Typography>

                <Grid container spacing={3}>
                    {/* Seleção de Evento */}
                    <Grid item xs={12} md={6}>
                        <Autocomplete
                            options={eventOptions}
                            loading={loadingEvents}
                            getOptionLabel={(option) => option.attributes?.name || ''}
                            value={eventOptions.find(e => e.attributes?.name === eventSelected) || null}
                            onChange={(_, newValue) => {
                                if (newValue) {
                                    setEventSelected(newValue.attributes.name);
                                } else {
                                    setEventSelected(null);
                                    setPlans([]);
                                    setSelectedPlan(null);
                                }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Selecionar Evento *"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingEvents && <CircularProgress color="inherit" size={20} />}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    {/* Seleção de Plano/Categoria */}
                    <Grid item xs={12} md={6}>
                        <Autocomplete
                            options={plans}
                            getOptionLabel={(option) => `${option.attributes?.type} | ${option.attributes?.price?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) || 0}`}
                            value={plans.find(p => p.id === selectedPlan) || null}
                            onChange={(_, newValue) => {
                                setSelectedPlan(newValue ? newValue.id : null);
                            }}
                            disabled={!eventSelected || plans.length === 0}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Selecionar Plano/Categoria *"
                                />
                            )}
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* Dados da Empresa */}
            <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    Dados da Empresa
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="CNPJ *"
                            value={cnpj}
                            onChange={(e) => handleCNPJChange(e.target.value)}
                            placeholder="00.000.000/0000-00"
                            inputProps={{ maxLength: 18 }}
                            disabled={loadingCompany}
                            InputProps={{
                                endAdornment: loadingCompany && <CircularProgress size={20} />
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={2}>
                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<SearchIcon />}
                            sx={{ height: '100%' }}
                            onClick={() => {
                                setCompanySearchName(razaoSocial || nomeFantasia || '');
                                setCompanySearchResults([]);
                                setCompanySearchAttempted(false);
                                setOpenCompanySearchModal(true);
                            }}
                        >
                            Buscar por Nome
                        </Button>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="Razão Social *"
                            value={razaoSocial}
                            onChange={(e) => setRazaoSocial(e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Nome Fantasia"
                            value={nomeFantasia}
                            onChange={(e) => setNomeFantasia(e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={2}>
                        <TextField
                            fullWidth
                            label="CEP *"
                            value={cep}
                            onChange={(e) => handleCepChange(e.target.value)}
                            placeholder="00000-000"
                            inputProps={{ maxLength: 9 }}
                            disabled={loadingCep}
                            InputProps={{
                                endAdornment: loadingCep && <CircularProgress size={20} />
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Endereço *"
                            value={endereco}
                            onChange={(e) => setEndereco(e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={2}>
                        <TextField
                            fullWidth
                            label="Número *"
                            value={numero}
                            onChange={(e) => setNumero(e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={2}>
                        <TextField
                            fullWidth
                            label="Bairro *"
                            value={bairro}
                            onChange={(e) => setBairro(e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <TextField
                            fullWidth
                            label="Cidade *"
                            value={cidade}
                            onChange={(e) => setCidade(e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={2}>
                        <TextField
                            fullWidth
                            label="Estado (UF) *"
                            value={estado}
                            onChange={(e) => setEstado(e.target.value.toUpperCase())}
                            placeholder="SP"
                            inputProps={{ maxLength: 2 }}
                        />
                    </Grid>

                    <Grid item xs={12} md={12}>
                        <TextField
                            fullWidth
                            label="E-mail da Empresa *"
                            type="email"
                            value={emailEmpresa}
                            onChange={(e) => setEmailEmpresa(e.target.value)}
                            placeholder="contato@empresa.com.br"
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* Inscritos */}
            <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                        Inscritos ({subscribers.length})
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleAddSubscriber}
                    >
                        Adicionar Inscrito
                    </Button>
                </Box>
                <Divider sx={{ mb: 3 }} />

                {subscribers.map((subscriber, index) => (
                    <Card key={subscriber.id} sx={{ mb: 2, bgcolor: '#fafafa' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Chip label={`Inscrito ${index + 1}`} color="primary" size="small" />
                                {subscribers.length > 1 && (
                                    <IconButton 
                                        color="error" 
                                        size="small"
                                        onClick={() => handleRemoveSubscriber(subscriber.id)}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                )}
                            </Box>

                            <Grid container spacing={2}>
                                <Grid item xs={12} md={2}>
                                    <TextField
                                        fullWidth
                                        label="CPF *"
                                        value={subscriber.document}
                                        onChange={(e) => handleSubscriberChange(subscriber.id, 'document', e.target.value)}
                                        placeholder="000.000.000-00"
                                        inputProps={{ maxLength: 14 }}
                                        disabled={loadingSubscriber[subscriber.id]}
                                        InputProps={{
                                            endAdornment: loadingSubscriber[subscriber.id] && <CircularProgress size={20} />
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Nome Completo *"
                                        value={subscriber.name}
                                        onChange={(e) => handleSubscriberChange(subscriber.id, 'name', e.target.value)}
                                    />
                                </Grid>

                                <Grid item xs={12} md={2}>
                                    <TextField
                                        fullWidth
                                        label="WhatsApp *"
                                        value={subscriber.whatsapp}
                                        onChange={(e) => handleSubscriberChange(subscriber.id, 'whatsapp', e.target.value)}
                                        placeholder="(00) 00000-0000"
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="E-mail"
                                        type="email"
                                        value={subscriber.email}
                                        onChange={(e) => handleSubscriberChange(subscriber.id, 'email', e.target.value)}
                                    />
                                </Grid>

                                <Grid item xs={12} md={2}>
                                    <TextField
                                        fullWidth
                                        label="Crachá"
                                        value={subscriber.badge}
                                        onChange={(e) => handleSubscriberChange(subscriber.id, 'badge', e.target.value)}
                                    />
                                </Grid>

                                <Grid item xs={12} md={2}>
                                    <TextField
                                        fullWidth
                                        label="Data de Nascimento"
                                        type="date"
                                        value={subscriber.birth}
                                        onChange={(e) => handleSubscriberChange(subscriber.id, 'birth', e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={2}>
                                    <TextField
                                        fullWidth
                                        label="Gênero"
                                        value={subscriber.gender}
                                        onChange={(e) => handleSubscriberChange(subscriber.id, 'gender', e.target.value)}
                                        select
                                        SelectProps={{ native: true }}
                                    >
                                        <option value=""></option>
                                        <option value="M">Masculino</option>
                                        <option value="F">Feminino</option>
                                        <option value="Outro">Outro</option>
                                    </TextField>
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <TextField
                                        fullWidth
                                        label="Cargo"
                                        value={subscriber.office}
                                        onChange={(e) => handleSubscriberChange(subscriber.id, 'office', e.target.value)}
                                    />
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <TextField
                                        fullWidth
                                        label="Tamanho da Camisa"
                                        value={subscriber.tshirtSize}
                                        onChange={(e) => handleSubscriberChange(subscriber.id, 'tshirtSize', e.target.value)}
                                        select
                                        SelectProps={{ native: true }}
                                    >
                                        <option value=""></option>
                                        <option value="PP">PP</option>
                                        <option value="P">P</option>
                                        <option value="M">M</option>
                                        <option value="G">G</option>
                                        <option value="GG">GG</option>
                                        <option value="XG">XG</option>
                                    </TextField>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                ))}
            </Paper>

            {/* Botão de Salvar */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                    variant="contained"
                    color="success"
                    size="large"
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    onClick={handleSubmit}
                    disabled={loading}
                >
                    {loading ? 'Salvando...' : 'Salvar Inscrição e Imprimir Cracha'}
                </Button>
            </Box>

            {/* Modal de Impressão */}
            <Dialog
                open={openPrintModal}
                onClose={handleCloseModal}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>Visualizar Crachás</DialogTitle>
                <DialogContent>
                    {pdfData && (
                        <iframe
                            ref={printIframeRef}
                            src={pdfData}
                            style={{ width: '100%', height: '600px', border: 'none' }}
                            title="PDF Preview"
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal} color="secondary">
                        Fechar
                    </Button>
                    <Button onClick={handlePrint} color="primary" variant="contained">
                        Imprimir
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openCompanySearchModal} onClose={() => setOpenCompanySearchModal(false)} fullWidth maxWidth="md">
                <DialogTitle>Pesquisar Empresa por Nome</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', gap: 1.5, mb: 2, mt: 1 }}>
                        <TextField
                            fullWidth
                            label="Razão Social / Nome Fantasia"
                            value={companySearchName}
                            onChange={(e) => setCompanySearchName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSearchCompanyByName();
                                }
                            }}
                        />
                        <Button
                            variant="contained"
                            onClick={handleSearchCompanyByName}
                            startIcon={companySearchLoading ? <CircularProgress size={16} /> : <SearchIcon />}
                            disabled={companySearchLoading}
                        >
                            Buscar
                        </Button>
                    </Box>

                    {companySearchLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={26} />
                        </Box>
                    ) : companySearchResults.length > 0 ? (
                        <Box sx={{ display: 'grid', gap: 1 }}>
                            {companySearchResults.map((company) => (
                                <Box
                                    key={company.id}
                                    sx={{
                                        border: '1px solid #ddd',
                                        borderRadius: 1,
                                        p: 1.5,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        gap: 1
                                    }}
                                >
                                    <Box>
                                        <Typography variant="body2" fontWeight={600}>{company.attributes?.social_reason || '-'}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {company.attributes?.trade || '-'} | {maskCNPJ(company.attributes?.document || '') || '-'}
                                        </Typography>
                                    </Box>
                                    <Button size="small" onClick={() => handleSelectCompanyFromSearch(company)}>
                                        Selecionar
                                    </Button>
                                </Box>
                            ))}
                        </Box>
                    ) : companySearchAttempted ? (
                        <Typography variant="body2" color="text.secondary">Nenhuma empresa encontrada para o nome informado.</Typography>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenCompanySearchModal(false)}>Fechar</Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
}
